// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import MoveChatModal from '@features/moves/components/MoveChatModal';
import { clearMoveReadStatus } from '@features/moves/services/smsReadStatusTracker';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

// TYPES ---------------------------------------------------------------- //

interface ChatModalState {
  isOpen: boolean;
  moveId?: number;
  consumerPickup?: boolean;
}

interface ChatModalContextValue {
  openChat: (moveId: number, consumerPickup?: boolean) => void;
  closeChat: () => void;
  markAsRead: (moveId: number) => void;
  isOpen: boolean;
  moveId?: number;
  consumerPickup?: boolean;
}

// CONTEXT ---------------------------------------------------------------- //

const ChatModalContext = React.createContext<ChatModalContextValue | null>(null);

// PROVIDER ---------------------------------------------------------------- //

interface ChatModalProviderProps {
  children: React.ReactNode;
}

export const ChatModalProvider = ({ children }: ChatModalProviderProps) => {
  const isSelfManaged = useValetSelfManaged();
  const [state, setState] = React.useState<ChatModalState>({
    isOpen: false,
    moveId: undefined,
    consumerPickup: undefined,
  });

  const openChat = React.useCallback((moveId: number, consumerPickup = true) => {
    // Chat is a self-managed feature.
    if (!isSelfManaged) {
      return;
    }

    setState({
      isOpen: true,
      moveId,
      consumerPickup,
    });
  }, [isSelfManaged]);

  const closeChat = React.useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const markAsRead = React.useCallback((moveId: number) => {
    // Clear the response status when a message is sent
    // This will be updated by the real-time subscription in useUnreadMessages
    clearMoveReadStatus(moveId);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      openChat,
      closeChat,
      markAsRead,
      isOpen: state.isOpen,
      moveId: state.moveId,
      consumerPickup: state.consumerPickup,
    }),
    [openChat, closeChat, markAsRead, state.isOpen, state.moveId, state.consumerPickup]
  );

  return (
    <ChatModalContext.Provider value={contextValue}>
      {children}
      
      {/* Global Chat Modal */}
      <MoveChatModal
        open={state.isOpen}
        onClose={closeChat}
        moveId={state.moveId}
        consumerPickup={state.consumerPickup}
      />
    </ChatModalContext.Provider>
  );
};

// HOOK ---------------------------------------------------------------- //

export const useChatModal = () => {
  const context = React.useContext(ChatModalContext);
  
  if (!context) {
    throw new Error('useChatModal must be used within a ChatModalProvider');
  }
  
  return context;
};

// EXPORT ---------------------------------------------------------------- //

export default ChatModalProvider;